import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Calendar, Clock, MapPin, User, MoreVertical, Video, Phone, Users } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import type { Event } from '@/types'
import moment from 'moment'

interface MobileEventCardProps {
  event: Event
  isSelected?: boolean
  onToggleSelect?: (id: string) => void
  onClick?: (event: Event) => void
  onEdit?: (event: Event) => void
  onDelete?: (event: Event) => void
}

export function MobileEventCard({
  event,
  isSelected = false,
  onToggleSelect,
  onClick,
  onEdit,
  onDelete,
}: MobileEventCardProps) {
  const startTime = moment(event.start_date_time)
  const endTime = moment(event.end_date_time)
  const isToday = startTime.isSame(moment(), 'day')
  const isPast = startTime.isBefore(moment())
  const duration = moment.duration(endTime.diff(startTime))
  const durationText =
    duration.hours() > 0 ? `${duration.hours()}h ${duration.minutes()}m` : `${duration.minutes()}m`

  // Determine event type icon
  const getEventTypeIcon = () => {
    if (event.type?.toLowerCase().includes('video') || event.is_video_call) {
      return <Video className="h-4 w-4" />
    }
    if (event.type?.toLowerCase().includes('call')) {
      return <Phone className="h-4 w-4" />
    }
    if (event.type?.toLowerCase().includes('meeting')) {
      return <Users className="h-4 w-4" />
    }
    return <Calendar className="h-4 w-4" />
  }

  return (
    <Card
      className={cn('mb-3 transition-all', isPast && 'opacity-60', isToday && 'border-primary')}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {onToggleSelect && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => onToggleSelect(event.id)}
              className="mt-1"
              aria-label={`Select event ${event.subject}`}
            />
          )}

          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onClick?.(event)}>
            {/* Header with Subject and Actions */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-start gap-2 flex-1 min-w-0">
                <div className="mt-0.5">{getEventTypeIcon()}</div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">
                    {event.subject || 'Untitled Event'}
                  </h3>
                  {event.type && (
                    <Badge variant="outline" className="mt-1 text-xs">
                      {event.type}
                    </Badge>
                  )}
                </div>
              </div>

              {(onEdit || onDelete) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">Event actions</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {onEdit && (
                      <DropdownMenuItem onClick={() => onEdit(event)}>Edit Event</DropdownMenuItem>
                    )}
                    {onDelete && (
                      <DropdownMenuItem
                        onClick={() => onDelete(event)}
                        className="text-destructive"
                      >
                        Delete Event
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Event Details */}
            <div className="space-y-2 text-xs text-muted-foreground">
              {/* Date and Time */}
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3" />
                <span className="font-medium">{isToday ? 'Today' : startTime.format('MMM D')}</span>
                <span>
                  {startTime.format('h:mm A')} - {endTime.format('h:mm A')}
                  {` (${durationText})`}
                </span>
              </div>

              {/* Location */}
              {event.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="h-3 w-3" />
                  <span className="truncate">{event.location}</span>
                </div>
              )}

              {/* Owner */}
              {event.owner_name && (
                <div className="flex items-center gap-2">
                  <User className="h-3 w-3" />
                  <span className="truncate">{event.owner_name}</span>
                </div>
              )}

              {/* Related To */}
              {event.related_to_name && (
                <div className="text-xs">
                  <span className="text-muted-foreground">Related to: </span>
                  <span className="font-medium">{event.related_to_name}</span>
                </div>
              )}

              {/* Description Preview */}
              {event.description && (
                <p className="line-clamp-2 text-xs mt-2">{event.description}</p>
              )}

              {/* Status Badges */}
              <div className="flex flex-wrap gap-1 mt-2">
                {event.is_all_day && (
                  <Badge variant="secondary" className="text-xs">
                    All Day
                  </Badge>
                )}
                {event.is_private && (
                  <Badge variant="secondary" className="text-xs">
                    Private
                  </Badge>
                )}
                {event.is_reminder_set && (
                  <Badge variant="secondary" className="text-xs">
                    Reminder Set
                  </Badge>
                )}
                {isPast && (
                  <Badge variant="outline" className="text-xs">
                    Past Event
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface MobileEventListProps {
  events: Event[]
  selectedIds?: Set<string>
  onToggleSelect?: (id: string) => void
  onClick?: (event: Event) => void
  onEdit?: (event: Event) => void
  onDelete?: (event: Event) => void
  groupByDate?: boolean
}

export function MobileEventList({
  events,
  selectedIds,
  onToggleSelect,
  onClick,
  onEdit,
  onDelete,
  groupByDate = true,
}: MobileEventListProps) {
  // Group events by date if requested
  const groupedEvents = useMemo(() => {
    if (!groupByDate) return { 'All Events': events }

    const groups: Record<string, Event[]> = {}

    events.forEach((event) => {
      const date = moment(event.start_date_time)
      let key: string

      if (date.isSame(moment(), 'day')) {
        key = 'Today'
      } else if (date.isSame(moment().add(1, 'day'), 'day')) {
        key = 'Tomorrow'
      } else if (date.isSame(moment().subtract(1, 'day'), 'day')) {
        key = 'Yesterday'
      } else if (date.isSame(moment(), 'week')) {
        key = date.format('dddd') // Day name for this week
      } else {
        key = date.format('MMM D, YYYY')
      }

      if (!groups[key]) {
        groups[key] = []
      }
      groups[key]!.push(event)
    })

    return groups
  }, [events, groupByDate])

  const sortedDates = Object.keys(groupedEvents).sort((a, b) => {
    // Special cases for relative dates
    const order = ['Yesterday', 'Today', 'Tomorrow']
    const aIndex = order.indexOf(a)
    const bIndex = order.indexOf(b)

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex
    }
    if (aIndex !== -1) return -1
    if (bIndex !== -1) return 1

    // For other dates, sort chronologically
    const aDate = moment(groupedEvents[a]?.[0]?.start_date_time)
    const bDate = moment(groupedEvents[b]?.[0]?.start_date_time)
    return aDate.diff(bDate)
  })

  if (events.length === 0) {
    return <div className="text-center py-8 text-muted-foreground">No events found</div>
  }

  return (
    <div className="space-y-4">
      {sortedDates.map((date) => (
        <div key={date}>
          {groupByDate && (
            <h3 className="text-sm font-semibold text-muted-foreground mb-2 sticky top-0 bg-background z-10 py-1">
              {date}
              <span className="ml-2 text-xs font-normal">
                ({groupedEvents[date]?.length ?? 0} event
                {(groupedEvents[date]?.length ?? 0) !== 1 ? 's' : ''})
              </span>
            </h3>
          )}
          <div className="space-y-2">
            {(groupedEvents[date] ?? []).map((event) => (
              <MobileEventCard
                key={event.id}
                event={event}
                isSelected={selectedIds?.has(event.id)}
                onToggleSelect={onToggleSelect}
                onClick={onClick}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// Export useMemo to be used by the component
import { useMemo } from 'react'
